import { create } from 'zustand';
import { candidateApi } from '@api/candidateApi';

const INITIAL_STATE = {
    candidates: [],
    interviewScheduledData: [],
    candidatePipeline: [],
    candidatePipelineError: false,
    loadingCandidates: false,
    loadingInterviewScheduledData: false,
    loadingCandidatePipeline: false,
    error: null,
    searchQuery: '',
    activeId: null,
    selectedCandidate: null,
    isDialogOpen: false,
};

const useCandidatePipelineStore = create((set, get) => ({
    ...INITIAL_STATE,

    setStoreState: newState => set(newState),

    getFilteredCandidates: () => {
        const { candidatePipeline, searchQuery } = get();
        if (!searchQuery.trim()) return candidatePipeline;

        const query = searchQuery.toLowerCase();
        return candidatePipeline.filter(
            candidate =>
                (candidate?.candidate_name || '').toLowerCase().includes(query) ||
                candidate.candidate_mob_no.includes(query) ||
                candidate.email.toLowerCase().includes(query),
        );
    },

    fetchCandidates: async (fresh = false) => {
        const state = get();
        if (state.loadingCandidates && !fresh) return;

        set({ loadingCandidates: true, error: null });
        try {
            const response = await candidateApi.getCandidate();
            const filteredCandidates = response.filter(
                candidate => (candidate?.candidate_name || '') !== null,
            );
            set({ candidates: filteredCandidates, loadingCandidates: false });
            return filteredCandidates;
        } catch (error) {
            const errorMessage = error.response?.data?.error || error.message;
            set({
                error: `Error ${error.response?.status || ''}: ${errorMessage}`,
                loadingCandidates: false,
            });
            return state.candidates; // Return existing candidates on error
        }
    },

    fetchInterviewScheduledData: async (isLoader = true) => {
        const state = get();
        if (state.loadingInterviewScheduledData) return;

        set({ loadingInterviewScheduledData: isLoader, error: null });
        try {
            const response = await candidateApi.getInterviewScheduledData();
            if (!response) return [];
            const filteredCandidates = response.filter(
                candidate => candidate.interview_scheduled_at !== null,
            );
            set({
                interviewScheduledData: filteredCandidates,
                loadingInterviewScheduledData: false,
            });
            return filteredCandidates;
        } catch (error) {
            const errorMessage = error.response?.data?.error || error.message;
            set({
                error: `Error ${error.response?.status || ''}: ${errorMessage}`,
                loadingInterviewScheduledData: false,
            });
            return state.interviewScheduledData; // Return existing candidates on error
        }
    },

    fetchCandidatePipeline: async (isLoader = true) => {
        const state = get();
        if (state.loadingCandidatePipeline) return;

        set({ loadingCandidatePipeline: isLoader, candidatePipelineError: null });
        try {
            const response = await candidateApi.getCandidatePipeline();
            if (!response) return [];
            const filteredCandidates = response.filter(
                candidate => candidate.is_shortlisted === 'yes',
            );
            set({ candidatePipeline: filteredCandidates, loadingCandidatePipeline: false });
            return filteredCandidates;
        } catch (error) {
            const errorMessage = error.response?.data?.error || error.message;
            set({
                candidatePipelineError: `Error ${error.response?.status || ''}: ${errorMessage}`,
                loadingCandidatePipeline: false,
            });
            return state.candidatePipeline; // Return existing candidates on error
        }
    },

    updateCandidateStatus: async (candidateId, newStatus) => {
        try {
            set(state => ({
                candidatePipeline: state.candidatePipeline.map(candidate =>
                    candidate.id === candidateId ? { ...candidate, status: newStatus } : candidate,
                ),
            }));
            await candidateApi.updateCandidateStatus(candidateId, newStatus);
            return true;
        } catch (error) {
            set({ error: error.message });
            return false;
        }
    },

    setSearchQuery: query => set({ searchQuery: query }),
    setActiveId: id => set({ activeId: id }),
    setDialogOpen: isOpen => set({ isDialogOpen: isOpen }),
    setSelectedCandidate: candidate => set({ selectedCandidate: candidate }),

    clearStore: () => set(INITIAL_STATE),
}));

export default useCandidatePipelineStore;
